import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, CalendarCheck, Mail, Inbox, Phone, MapPin, Sparkles, RefreshCw,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const STATUS = ["new", "contacted", "confirmed", "completed", "cancelled"];

function StatusSelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-9 w-36 capitalize">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS.map((s) => (
          <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/contacts"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setContacts(c.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role) load();
  }, [user, load]);

  const updateBooking = async (id, status) => {
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Booking updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };
  const updateContact = async (id, status) => {
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, status } : c)));
    try {
      await api.patch(`/admin/contacts/${id}`, { status });
      toast.success("Enquiry updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (user === null) {
    return <div className="min-h-screen grid place-items-center bg-mist text-navy">Loading...</div>;
  }

  const cards = [
    { label: "Total Bookings", value: stats?.total_bookings ?? 0, icon: CalendarCheck },
    { label: "New Bookings", value: stats?.new_bookings ?? 0, icon: Inbox },
    { label: "Total Enquiries", value: stats?.total_contacts ?? 0, icon: Mail },
    { label: "New Enquiries", value: stats?.new_contacts ?? 0, icon: Inbox },
  ];

  return (
    <div className="min-h-screen bg-mist" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="bg-navy text-white">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-brandgreen"><Sparkles className="w-5 h-5" /></span>
            <div className="leading-tight">
              <span className="block font-heading font-extrabold tracking-tight">Mark Clean Admin</span>
              <span className="block text-xs text-white/60">{user?.email}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={load} variant="ghost" size="sm" data-testid="refresh-btn" className="text-white hover:bg-white/10">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button onClick={doLogout} data-testid="admin-logout" className="rounded-full bg-white/10 hover:bg-brandgreen text-white">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8 lg:py-12">
        {/* Stat cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-10">
          {cards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-white border border-black/5 p-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{c.label}</span>
                <c.icon className="w-5 h-5 text-brandgreen" />
              </div>
              <div className="mt-3 font-heading font-extrabold text-navy text-4xl tracking-tighter">{c.value}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Enquiries ({contacts.length})</TabsTrigger>
          </TabsList>

          {/* Bookings */}
          <TabsContent value="bookings">
            <div className="rounded-2xl bg-white border border-black/5 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Date / Time</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No bookings yet.</TableCell></TableRow>
                  ) : bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                      <TableCell>
                        <div className="font-semibold text-navy">{b.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{b.address}</div>
                      </TableCell>
                      <TableCell className="font-medium">{b.service}</TableCell>
                      <TableCell className="text-sm">{b.preferred_date}<br /><span className="text-muted-foreground">{b.preferred_time}</span></TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-brandgreen" />{b.telephone}</div>
                        <div className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" />{b.email}</div>
                      </TableCell>
                      <TableCell className="max-w-[200px] text-xs text-muted-foreground">{b.special_instructions || "—"}</TableCell>
                      <TableCell><StatusSelect value={b.status} onChange={(v) => updateBooking(b.id, v)} testid={`booking-status-${b.id}`} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Contacts */}
          <TabsContent value="contacts">
            <div className="rounded-2xl bg-white border border-black/5 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No enquiries yet.</TableCell></TableRow>
                  ) : contacts.map((c) => (
                    <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                      <TableCell className="font-semibold text-navy">{c.name}</TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1 text-muted-foreground"><Mail className="w-3 h-3" />{c.email}</div>
                        {c.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-brandgreen" />{c.phone}</div>}
                      </TableCell>
                      <TableCell className="capitalize">{c.type}</TableCell>
                      <TableCell className="max-w-[320px] text-sm text-muted-foreground">{c.message}</TableCell>
                      <TableCell><StatusSelect value={c.status} onChange={(v) => updateContact(c.id, v)} testid={`contact-status-${c.id}`} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
